import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Check from '@mui/icons-material/Check';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Block from '@mui/icons-material/Block';
import Tune from '@mui/icons-material/Tune';
import FilterAltOff from '@mui/icons-material/FilterAltOff';
import MeetingRoomOutlined from '@mui/icons-material/MeetingRoomOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PeopleOutline from '@mui/icons-material/PeopleOutline';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import Flight from '@mui/icons-material/Flight';
import TextFields from '@mui/icons-material/TextFields';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import Search from '@mui/icons-material/Search';
import {
  DEFAULT_LOG_SEARCH_OPTIONS,
  LogSearchOptions,
} from '../redux';
import { isDefaultLogSearchOptions } from '../logSearch';
import * as S from './ActivitySelector.styled';

type MultiOptionKey = keyof Pick<
  LogSearchOptions,
  | 'exactMatch'
  | 'negate'
  | 'hideArchives'
  | 'fromFullLogs'
>;

const MULTI_OPTIONS: {
  key: MultiOptionKey,
  title: string,
  hint: string,
  icon: React.ReactNode,
  requiresPriorSearch?: boolean,
}[] = [
  {
    key: 'exactMatch',
    title: 'Exact match',
    hint: 'Require the full term, not a partial match',
    icon: <TextFields fontSize="inherit" />,
  },
  {
    key: 'negate',
    title: 'Exclude matches',
    hint: 'Show everything except what matches',
    icon: <Block fontSize="inherit" />,
  },
  {
    key: 'fromFullLogs',
    title: 'Add from prior results',
    hint: 'Search the results from before the last search and add matches',
    icon: <LibraryAdd fontSize="inherit" />,
    requiresPriorSearch: true,
  },
  {
    key: 'hideArchives',
    title: 'Hide archives',
    hint: 'Skip removed inventory items, groups, and deleted departments',
    icon: <DeleteOutline fontSize="inherit" />,
  },
];

export const chipModeIcon = (options: LogSearchOptions) => {
  if (options.matchMode === 'room') return <MeetingRoomOutlined fontSize="inherit" />;
  if (options.matchMode === 'staff') return <PersonOutline fontSize="inherit" />;
  if (options.matchMode === 'customer') return <PeopleOutline fontSize="inherit" />;
  if (options.matchMode === 'department') return <AccountTreeOutlined fontSize="inherit" />;
  if (options.matchMode === 'ota') return <Flight fontSize="inherit" />;
  if (options.fromFullLogs) return <LibraryAdd fontSize="inherit" />;
  if (options.negate) return <Block fontSize="inherit" />;
  return <Search fontSize="inherit" />;
};

type LogSearchOptionsMenuProps = {
  options: LogSearchOptions,
  onChange: (next: LogSearchOptions) => void,
  compact?: boolean,
  canClearAll?: boolean,
  onClearAll?: () => void,
  allowFromFullLogs?: boolean,
};

const LogSearchOptionsMenu = ({
  options,
  onChange,
  compact,
  canClearAll,
  onClearAll,
  allowFromFullLogs,
}: LogSearchOptionsMenuProps) => {
  const [open, setOpen] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const custom = !isDefaultLogSearchOptions(options);
  const canClear = custom || Boolean(canClearAll);
  const filterOptions = MULTI_OPTIONS.filter(
    (opt) => !opt.requiresPriorSearch || allowFromFullLogs,
  );

  const updatePopupPos = () => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;
    const rect = anchor.getBoundingClientRect();
    const popupH = popup.offsetHeight;
    const popupW = popup.offsetWidth;
    const gap = 6;
    let top = rect.bottom + gap;
    if (top + popupH > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popupH - gap);
    }
    let left = rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - popupW - 8));
    setPopupPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPopupPos(null);
      return undefined;
    }
    updatePopupPos();
    const onReposition = () => updatePopupPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, options, canClear, allowFromFullLogs]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!anchorRef.current?.contains(e.target as Node)
        && !popupRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggleMulti = (key: MultiOptionKey) => {
    onChange({ ...options, [key]: !options[key] });
  };

  const clearAll = () => {
    if (!canClear) return;
    onChange({ ...DEFAULT_LOG_SEARCH_OPTIONS });
    onClearAll?.();
    setOpen(false);
  };

  return (
    <S.SearchOptionsAnchor ref={anchorRef}>
      <S.SearchOptionsBtn
        type="button"
        $open={open}
        $custom={custom}
        $compact={compact}
        aria-label="Search options"
        aria-expanded={open}
        title={custom ? 'Custom search options' : 'Search options'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <Tune fontSize="inherit" />
      </S.SearchOptionsBtn>
      {open && (
        <S.SearchOptionsPopup
          ref={popupRef}
          role="menu"
          style={popupPos
            ? { top: popupPos.top, left: popupPos.left }
            : { visibility: 'hidden' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <S.SearchOptionsSectionLabel>Filters</S.SearchOptionsSectionLabel>
          <S.SearchOptionsSection>
            {filterOptions.map((opt) => {
              const checkedOpt = Boolean(options[opt.key]);
              return (
                <S.SearchOptionRow
                  key={opt.key}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={checkedOpt}
                  $checked={checkedOpt}
                  onClick={() => toggleMulti(opt.key)}
                >
                  <S.SearchOptionCheck $checked={checkedOpt}>
                    {checkedOpt ? <Check fontSize="inherit" /> : null}
                  </S.SearchOptionCheck>
                  <S.SearchOptionIcon>{opt.icon}</S.SearchOptionIcon>
                  <S.SearchOptionText>
                    <S.SearchOptionTitle>{opt.title}</S.SearchOptionTitle>
                    <S.SearchOptionHint>{opt.hint}</S.SearchOptionHint>
                  </S.SearchOptionText>
                </S.SearchOptionRow>
              );
            })}
          </S.SearchOptionsSection>
          <S.SearchOptionsDivider />
          <S.SearchOptionsClearBtn
            type="button"
            $disabled={!canClear}
            disabled={!canClear}
            aria-label="Clear all search"
            title="Clear all search"
            onClick={clearAll}
          >
            <FilterAltOff fontSize="inherit" />
            Clear all
          </S.SearchOptionsClearBtn>
        </S.SearchOptionsPopup>
      )}
    </S.SearchOptionsAnchor>
  );
};

export default LogSearchOptionsMenu;
